import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Card, Button, message, Alert } from "antd";
import AgGridWithColumnState from "../../../../components/AgGridWithColumnState";
import { DownloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import useGlobalFilters from "../../../../../hooks/useGlobalFilters";
import { getLetterProcessing } from "../../../api/accounting";

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";

const LetterProcessingRecords = () => {
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);

  const profile = useSelector((state) => state.profile);
  const { brandIds, hasSelectedBrands } = useGlobalFilters();

  // Check if user has permission to view letter processing records
  // Allow admin, super_admin, senior_accountant, and management roles
  // Also check page permissions if available
  const hasAccess = useMemo(() => {
    if (!profile) return false;

    const role = profile.role;
    // Allow these roles
    if (
      role === "admin" ||
      role === "super_admin" ||
      role === "senior_accountant" ||
      role === "management"
    ) {
      return true;
    }

    // Check page permissions if available
    if (profile.page_permissions) {
      const route = "/accounting/letter-processing-records";
      const permissions = profile.page_permissions[route] || [];
      return permissions.includes("view");
    }

    return false;
  }, [profile]);

  // Column definitions for AG Grid
  const columnDefs = useMemo(
    () => [
      {
        headerName: "Date",
        field: "date",
        sortable: true,
        filter: "agDateColumnFilter",
        width: 120,
        valueFormatter: (params) => {
          return params.value ? dayjs(params.value).format("YYYY-MM-DD") : "";
        },
      },
      {
        headerName: "Brand",
        field: "brand_name",
        sortable: true,
        filter: "agTextColumnFilter",
        width: 150,
      },
      {
        headerName: "Source",
        field: "source",
        sortable: true,
        filter: "agTextColumnFilter",
        width: 200,
      },
      {
        headerName: "Tracking Number",
        field: "tracking_number",
        sortable: true,
        filter: "agTextColumnFilter",
        width: 150,
      },
      {
        headerName: "Letters Received",
        field: "letters_received",
        sortable: true,
        filter: "agNumberColumnFilter",
        width: 140,
        type: "numericColumn",
      },
      {
        headerName: "Letters Processed",
        field: "letters_processed",
        sortable: true,
        filter: "agNumberColumnFilter",
        width: 140,
        type: "numericColumn",
      },
      {
        headerName: "Check Orders",
        field: "check_orders",
        sortable: true,
        filter: "agNumberColumnFilter",
        width: 120,
        type: "numericColumn",
      },
      {
        headerName: "Check Orders Value",
        field: "check_orders_value",
        sortable: true,
        filter: "agNumberColumnFilter",
        width: 150,
        type: "numericColumn",
        valueFormatter: (params) => {
          return params.value
            ? `$${parseFloat(params.value).toFixed(2)}`
            : "$0.00";
        },
      },
      {
        headerName: "Money Orders",
        field: "money_orders",
        sortable: true,
        filter: "agNumberColumnFilter",
        width: 120,
        type: "numericColumn",
      },
      {
        headerName: "Money Orders Value",
        field: "money_orders_value",
        sortable: true,
        filter: "agNumberColumnFilter",
        width: 150,
        type: "numericColumn",
        valueFormatter: (params) => {
          return params.value
            ? `$${parseFloat(params.value).toFixed(2)}`
            : "$0.00";
        },
      },
      {
        headerName: "CC Orders",
        field: "cc_orders",
        sortable: true,
        filter: "agNumberColumnFilter",
        width: 120,
        type: "numericColumn",
      },
      {
        headerName: "CC Orders Value",
        field: "cc_orders_value",
        sortable: true,
        filter: "agNumberColumnFilter",
        width: 150,
        type: "numericColumn",
        valueFormatter: (params) => {
          return params.value
            ? `$${parseFloat(params.value).toFixed(2)}`
            : "$0.00";
        },
      },
      {
        headerName: "CC Declined",
        field: "cc_declined",
        sortable: true,
        filter: "agNumberColumnFilter",
        width: 120,
        type: "numericColumn",
      },
      {
        headerName: "Cash Orders",
        field: "cash_orders",
        sortable: true,
        filter: "agNumberColumnFilter",
        width: 120,
        type: "numericColumn",
      },
      {
        headerName: "Cash Orders Value",
        field: "cash_orders_value",
        sortable: true,
        filter: "agNumberColumnFilter",
        width: 150,
        type: "numericColumn",
        valueFormatter: (params) => {
          return params.value
            ? `$${parseFloat(params.value).toFixed(2)}`
            : "$0.00";
        },
      },
      {
        headerName: "DNM",
        field: "dnm",
        sortable: true,
        filter: "agNumberColumnFilter",
        width: 100,
        type: "numericColumn",
      },
      {
        headerName: "Miscellaneous",
        field: "misc",
        sortable: true,
        filter: "agNumberColumnFilter",
        width: 120,
        type: "numericColumn",
        valueFormatter: (params) => {
          return params.value
            ? `$${parseFloat(params.value).toFixed(2)}`
            : "$0.00";
        },
      },
      {
        headerName: "Total Amount",
        field: "total_amount",
        sortable: true,
        filter: "agNumberColumnFilter",
        width: 130,
        type: "numericColumn",
        valueFormatter: (params) => {
          return params.value
            ? `$${parseFloat(params.value).toFixed(2)}`
            : "$0.00";
        },
        cellStyle: { fontWeight: "bold" },
      },
      {
        headerName: "Total Orders",
        field: "total_orders_processed",
        sortable: true,
        filter: "agNumberColumnFilter",
        width: 120,
        type: "numericColumn",
      },
      {
        headerName: "Created At",
        field: "created_at",
        sortable: true,
        filter: "agDateColumnFilter",
        width: 150,
        valueFormatter: (params) => {
          return params.value
            ? dayjs(params.value).format("YYYY-MM-DD HH:mm")
            : "";
        },
      },
    ],
    []
  );

  // Default column properties
  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      sortable: true,
      filter: true,
      floatingFilter: false, // Disable inline filter inputs
    }),
    []
  );

  // Memoize brandIds to prevent infinite loops
  const brandIdsKey = useMemo(() => brandIds.join(","), [brandIds]);

  // Fetch records function
  const fetchRecords = useCallback(async () => {
    if (!brandIds || brandIds.length === 0) {
      setRowData([]);
      setTotalRecords(0);
      return;
    }

    setLoading(true);

    try {
      const apiFilters = {
        brand: brandIds.join(","),
      };

      const response = await getLetterProcessing(apiFilters);
      const records =
        response?.data?.results || response?.results || response?.data || [];

      setRowData(records);
      setTotalRecords(records.length);
    } catch (error) {
      console.error("Error fetching letter processing records:", error);
      message.error("Failed to fetch letter processing records");
      setRowData([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [brandIds]);

  // Load data when brandIdsKey changes (memoized string, not array)
  useEffect(() => {
    if (brandIds.length > 0) {
      fetchRecords();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandIdsKey]); // Using brandIdsKey to avoid infinite loops

  // Export functionality (placeholder)
  const handleExport = () => {
    message.info("Export functionality will be implemented soon");
  };

  // Check permissions
  if (!hasAccess) {
    return (
      <div className="container-fluid">
        <Alert
          message="Access Denied"
          description="You don't have permission to view Letter Processing Records. Please contact your administrator to grant access."
          type="error"
          showIcon
        />
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {!hasSelectedBrands && (
        <Alert
          message="Please select a brand"
          description="You need to select at least one brand to view letter processing records."
          type="warning"
          showIcon
          className="mb-4"
        />
      )}

      <Card
        title={
          <div className="d-flex justify-content-between align-items-center">
            <span>Letter Processing Records</span>
            <span className="text-muted small">
              {totalRecords} record{totalRecords !== 1 ? "s" : ""}
            </span>
          </div>
        }
        extra={
          <Button
            icon={<DownloadOutlined />}
            onClick={handleExport}
            disabled={rowData.length === 0}
          >
            Export
          </Button>
        }
      >
        {/* AG Grid Table */}
        <div
          className="ag-theme-alpine"
          style={{ height: "600px", width: "100%" }}
        >
          <AgGridWithColumnState
            tableId="letter-processing-records"
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            pagination={true}
            paginationPageSize={50}
            loading={loading}
            suppressRowClickSelection={true}
            rowSelection="multiple"
            animateRows={true}
            enableRangeSelection={true}
            suppressMenuHide={true}
          />
        </div>
      </Card>
    </div>
  );
};

export default LetterProcessingRecords;
